import React, { useState, useEffect, useContext } from "react";

import { Draggable } from "react-beautiful-dnd";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import ListItemButton from "@mui/material/ListItemButton";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// My Files
import UserAvatar from "../../../management/UserAvatar";
import { UserContext } from "../../../../context";
import firebase from "../../../../firebase";
import MyFolderIcon from "./MyFolderIcon";
import ConfirmDeleteDialog from "../../../ConfirmDeleteDialog";
import DialogWithTextInput from "../../../DialogWithTextInput";
import {
    FinderFolderContextMenu,
    FinderRowActionButton,
} from "./FinderActionMenus";

const folderRowBase = (theme) => ({
    width: "100%",
    height: 60,
    padding: "8px 10px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    boxSizing: "border-box",
    cursor: "pointer",
    fontFamily: "inherit",
    "&.MuiButtonBase-root": {
        borderRadius: 10,
        overflow: "hidden",
    },
    transition:
        "background-color 220ms cubic-bezier(0.2, 0, 0, 1), color 180ms ease, border-color 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 180ms ease, transform 150ms cubic-bezier(0.2, 0, 0, 1)",
    "& .MuiTouchRipple-rippleVisible": {
        opacity: "0.12 !important",
        animationDuration: "300ms !important",
    },
    "& .MuiAvatar-root": {
        width: 40,
        height: 40,
        flexShrink: 0,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 8,
        transition:
            "background-color 220ms ease, color 180ms ease, transform 180ms cubic-bezier(0.2, 0, 0, 1)",
    },
    "&:active": {
        transform: "translateY(1px) scale(0.985)",
    },
    "&:focus-visible": {
        outline: "3px solid rgba(0, 149, 61, 0.28)",
        outlineOffset: 1,
    },
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
        "& .MuiAvatar-root, & $folderIconTile, & $arrowIcon": {
            transition: "none",
        },
        "&:hover, &:active": {
            transform: "none",
        },
        "&:hover .MuiAvatar-root, &:hover $folderIconTile, &:hover $arrowIcon": {
            transform: "none",
        },
        "& .MuiTouchRipple-rippleVisible": {
            animationDuration: "1ms !important",
        },
    },
});

const useStyles = makeStyles((theme) => ({
    container: {
        margin: 5,
    },
    white: {
        ...folderRowBase(theme),
        "&&": {
            color: theme.palette.text.primary,
            backgroundColor:
                theme.palette.mode === "dark"
                    ? theme.palette.background.subtle
                    : "rgb(245, 245, 245)",
        },
        "&&:hover": {
            backgroundColor: theme.palette.background.media,
            borderColor: theme.palette.text.disabled,
            boxShadow:
                theme.palette.mode === "dark"
                    ? "0 3px 10px rgba(0, 0, 0, 0.24)"
                    : "0 3px 10px rgba(20, 35, 25, 0.07)",
            transform: "translateY(-1px)",
            "& .MuiAvatar-root, & $folderIconTile": {
                transform: "scale(1.04)",
            },
            "& $arrowIcon": {
                transform: "translateX(2px)",
            },
        },
    },
    green: {
        ...folderRowBase(theme),
        "&&": {
            color: "white",
            backgroundColor:
                theme.palette.mode === "dark"
                    ? "rgb(0, 128, 53)"
                    : "rgb(0, 149, 61)",
            borderColor:
                theme.palette.mode === "dark"
                    ? "rgb(0, 128, 53)"
                    : "rgb(0, 149, 61)",
            boxShadow: "0 4px 12px rgba(0, 105, 43, 0.18)",
        },
        "& .MuiAvatar-root": {
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
        },
        "& $folderIconTile": {
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
        },
        "&&:hover": {
            backgroundColor: "rgb(0, 137, 56)",
            borderColor: "rgb(0, 137, 56)",
            boxShadow: "0 5px 14px rgba(0, 105, 43, 0.24)",
            transform: "translateY(-1px)",
            "& .MuiAvatar-root, & $folderIconTile": {
                transform: "scale(1.04)",
            },
            "& $arrowIcon": {
                transform: "translateX(2px)",
            },
        },
    },
    grey: {
        ...folderRowBase(theme),
        "&&": {
            color: theme.palette.text.primary,
            backgroundColor:
                theme.palette.mode === "dark" ? "#29342d" : "#e1e5e2",
            borderColor: theme.palette.divider,
        },
        "& $folderIconTile": {
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
        },
        "&&:hover": {
            backgroundColor:
                theme.palette.mode === "dark" ? "#313e35" : "#d9dfda",
            borderColor: theme.palette.text.disabled,
            boxShadow:
                theme.palette.mode === "dark"
                    ? "0 3px 10px rgba(0, 0, 0, 0.22)"
                    : "0 3px 10px rgba(20, 35, 25, 0.06)",
            transform: "translateY(-1px)",
            "& .MuiAvatar-root, & $folderIconTile": {
                transform: "scale(1.04)",
            },
            "& $arrowIcon": {
                transform: "translateX(2px)",
            },
        },
    },
    label: {
        minWidth: 0,
        flex: 1,
        margin: 0,
        marginLeft: 12,
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        fontSize: 16,
        lineHeight: 1.25,
        overflowWrap: "break-word",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    folderIconTile: {
        width: 40,
        height: 40,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 8,
        transition:
            "background-color 220ms ease, color 180ms ease, transform 180ms cubic-bezier(0.2, 0, 0, 1)",
    },
    folderIcon: {
        fontSize: "26px !important",
        padding: 0,
        margin: 0,
    },
    arrowIcon: {
        marginLeft: "auto",
        flexShrink: 0,
        fontSize: "18px !important",
        opacity: 0.82,
        transition:
            "color 180ms ease, opacity 180ms ease, transform 180ms cubic-bezier(0.2, 0, 0, 1)",
    },
    dragging: {
        "&&": {
            zIndex: 5,
            transform: "scale(1.025)",
            borderColor: "rgba(0, 149, 61, 0.38)",
            boxShadow:
                theme.palette.mode === "dark"
                    ? "0 16px 34px rgba(0, 0, 0, 0.42)"
                    : "0 16px 34px rgba(20, 35, 25, 0.2)",
        },
        "@media (prefers-reduced-motion: reduce)": {
            "&&": {
                transform: "none",
            },
        },
    },
}));

export default function FolderListItem(props) {
    const classes = useStyles();
    const history = useHistory();

    const [clickLocation, setClickLocation] = useState({
        mouseX: null,
        mouseY: null,
    });

    const { currentUserData } = useContext(UserContext);

    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const [folderNameDialogOpen, setFolderNameDialogOpen] = useState(false);
    const [isHub, setIsHub] = useState(false);

    const [isSelected, setIsSelected] = useState(false); // Folder is selected when user right clicks on it

    const [userIsAdmin, setUserIsAdmin] = useState(false);

    const [editingEnabled, setEditingEnabled] = useState(true); // Only false when we are embedding this is a part of the app just used to select files (e.g. in the projects section)

    useEffect(() => {
        if (props.folder.hub) {
            setIsHub(true);
        } else {
            setIsHub(false);
        }

        if (currentUserData) {
            if (
                props.folder.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager" ||
                props.folder.createdBy === currentUserData.key
            ) {
                setUserIsAdmin(true);
            } else {
                setUserIsAdmin(false);
            }
        }

        if (props.embedded === true) {
            setEditingEnabled(false);
        }

    }, [props, currentUserData]);

    function handleClick() {
        props.handleClick(props.index);
    }

    const handleRightClick = (event) => {
        if (editingEnabled === true) {
            event.preventDefault();

            // Checks that the current user is a manager for the folder or hub
            if (userIsAdmin) {
                setClickLocation({
                    mouseX: event.clientX - 2,
                    mouseY: event.clientY - 4,
                });

                setIsSelected(true);
            }
        }
    };

    const handleActionButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (editingEnabled && userIsAdmin) {
            const buttonBounds = event.currentTarget.getBoundingClientRect();

            setClickLocation({
                mouseX: buttonBounds.right,
                mouseY: buttonBounds.bottom + 4,
            });
            setIsSelected(true);
        }
    };

    const handleMenuAction = (action) => {
        if (action === "renamefolder") {
            handleFolderRenameDialogOpen();
        } else if (action === "deletefolder") {
            handleConfirmDeleteDialogOpen();
        } else if (action === "editusers") {
            // handleEditUsersDialogOpen();
        } else if (action === "edithub") {
            history.push("/managehub?key=" + props.folder.key);
        } else if (action === "archive") {
            navigator.clipboard.writeText(props.folder.key);
        }

        setClickLocation({
            mouseX: null,
            mouseY: null,
        });

        setIsSelected(false);
    };

    const handleFolderRenameDialogOpen = () => {
        setFolderNameDialogOpen(true);
    };

    const handleConfirmDeleteDialogOpen = () => {
        setConfirmDeleteDialogOpen(true);
    };

    const handleFolderRenameDialogClose = (folderName, confirmed) => {
        setFolderNameDialogOpen(false);

        if (editingEnabled && confirmed && folderName.trim() !== "") {
            let ref = firebase
                .database()
                .ref("folders")
                .child(props.folder.key);

            ref.update({ name: folderName.trim() });
        }
    };

    const handleConfirmDeleteDialogClose = (confirmed) => {
        setConfirmDeleteDialogOpen(false);

        if (editingEnabled && confirmed) {
            props.deleteFolder(props.folder);
        }
    };

    const rowClassName =
        props.selectionState === "current"
            ? classes.green
            : props.selectionState === "ancestor"
              ? classes.grey
              : classes.white;

    return (
        <Draggable
            draggableId={props.folder.id}
            index={props.index}
            isDragDisabled={
                currentUserData.role === "Player" ||
                currentUserData.role === "FAW Player" ||
                currentUserData.role === "Club Player" ||
                currentUserData.role === "Coach Ed Student" ||
                currentUserData.role === "FAW Coach Ed Student" ||
                currentUserData.role === "Club Coach Ed Student" ||
                currentUserData.role === "FAW Parent" ||
                currentUserData.role === "Club Parent" ||
                currentUserData.role === "Referee"
            }
        >
            {(provided, snapshot) => (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={classes.container}
                    ref={provided.innerRef}
                    onContextMenu={handleRightClick}
                >
                    <ListItemButton
                        key={props.folder.id}
                        className={`${rowClassName} ${
                            snapshot.isDragging ? classes.dragging : ""
                        }`}
                        onClick={handleClick}
                        aria-current={
                            props.selectionState === "current"
                                ? "page"
                                : undefined
                        }
                        data-selection-state={props.selectionState}
                        style={
                            isSelected
                                ? {
                                      boxShadow:
                                          "0 0 0 2px rgba(0, 149, 61, 0.48)",
                                  }
                                : {}
                        }
                    >
                        {props.folder.hub ? (
                            <UserAvatar
                                userOrHub={props.folder}
                                path="hubs"
                                variant="rounded"
                            />
                        ) : (
                            <span className={classes.folderIconTile}>
                                <MyFolderIcon
                                    className={classes.folderIcon}
                                    folderKey={props.folder.key}
                                />
                            </span>
                        )}
                        <p className={classes.label}>{props.folder.name}</p>
                        <ArrowForwardIosIcon className={classes.arrowIcon} />
                        {editingEnabled && userIsAdmin && (
                            <FinderRowActionButton
                                label={`Actions for ${props.folder.name}`}
                                onClick={handleActionButtonClick}
                            />
                        )}
                    </ListItemButton>
                    <>
                        {editingEnabled && (
                            <FinderFolderContextMenu
                                open={clickLocation.mouseY !== null}
                                position={{
                                    top: clickLocation.mouseY,
                                    left: clickLocation.mouseX,
                                }}
                                onClose={() => handleMenuAction()}
                                onAction={handleMenuAction}
                                isHub={isHub}
                            />
                        )}
                        {editingEnabled && (
                            <DialogWithTextInput
                                open={folderNameDialogOpen}
                                onClose={handleFolderRenameDialogClose}
                                initialValue={props.folder.name}
                                title="Rename Folder"
                                message="Enter a new name for this folder."
                                confirmLabel="Rename"
                                modern
                            />
                        )}
                        {editingEnabled && (
                            <ConfirmDeleteDialog
                                open={confirmDeleteDialogOpen}
                                onClose={handleConfirmDeleteDialogClose}
                                title="Delete Folder"
                                detail={props.folder.name}
                                message="Are you sure you want to delete this folder?"
                            />
                        )}
                    </>
                </div>
            )}
        </Draggable>
    );
}
